import { useEffect, useState } from "react"
import { Beauty, GlassesEffect } from "services/player"
import { ProductV2 } from "types"
import { Box, Loader } from "@strapi/design-system"
import { createPlayer, mount } from "../../services/player/player"
import { fractionalRgbToHex, hexOrRgbToRgbString } from "../ColorItem/helpers"
import { ProductCategories } from "components/Navbar/types"

interface TintViewerProps {
  productItem?: ProductV2
  image: string
  size: number
  activeColor?: string
  effectType?: ProductCategories
}

export const TintViewer = ({
  productItem,
  image,
  size = 260,
  activeColor,
  effectType,
}: TintViewerProps) => {
  const hideComparison = effectType === ProductCategories.GLASSES
  const [player, setPlayer] = useState<ReturnType<typeof createPlayer> | null>(null)
  const [ready, setReady] = useState(false)
  const [startEffects, setStartEffects] = useState(false)
  const [sliderValue, setSliderValue] = useState(50)
  const [showDelayedImage, setShowDelayedImage] = useState(false)
  const [transitionStyle, setTransitionStyle] = useState("opacity 0.5s ease-in-out")
  const [isDragging, setIsDragging] = useState(false)

  useEffect(() => {
    if (!player) setPlayer(createPlayer())
    return () => {
      if (player) {
        Beauty.reset()
        setReady(false)
      }
    }
  }, [player])

  useEffect(() => {
    if (ready && player) {
      mount("#webar")
      setStartEffects(true)
    }
  }, [ready, player])

  useEffect(() => {
    if (player)
      player.then(() => {
        Beauty.applyImage(image)
        setReady(true)
      })
  }, [image, player])

  useEffect(() => {
    setTransitionStyle("none")
    setShowDelayedImage(false)

    const enableTransitionTimer = setTimeout(() => {
      setTransitionStyle("opacity 0.5s ease-in-out")
    }, 50)

    const timer = setTimeout(() => {
      setShowDelayedImage(true)
    }, 1000)

    return () => {
      clearTimeout(timer)
      clearTimeout(enableTransitionTimer)
    }
  }, [image])

  const applyMakeupEffect = () => {
    const { region, finish, coverage } = productItem
    const makeupRegion = `makeup_${region}`
    const color = activeColor ? fractionalRgbToHex(hexOrRgbToRgbString(activeColor)) : undefined
    Beauty.setMakeupState(makeupRegion, finish, coverage, color)
  }

  const applyCustomGlasses = async () => {
    try {
      const glasses = productItem
      if (
        productItem?.effectUrl &&
        (!glasses.coverLeft || !glasses.coverFront || !glasses.coverRight)
      ) {
        await GlassesEffect.applyCustomGlassesEffectFromUrl(productItem.effectUrl)
      } else {
        const { coverLeft, coverFront, coverRight, renderingOptions } = productItem
        if (coverFront && coverLeft && coverRight)
          await GlassesEffect.applyCustomGlassesEffect({
            coverLeft,
            coverFront,
            coverRight,
            renderingOptions,
          })
        else Beauty.reset()
      }
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    if (!startEffects || !player || !productItem) return
    if (effectType === ProductCategories.MAKEUP) applyMakeupEffect()
    else if (effectType === ProductCategories.GLASSES) applyCustomGlasses()
  }, [
    effectType,
    productItem?.region,
    productItem?.finish,
    productItem?.coverage,
    productItem?.coverLeft,
    productItem?.coverFront,
    productItem?.coverRight,
    activeColor,
    player,
    startEffects,
  ])

  const handleMouseDown = () => {
    setIsDragging(true)
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDragging) {
      const rect = e.currentTarget.getBoundingClientRect()
      const offsetX = e.clientX - rect.left
      const newValue = Math.max(0, Math.min(100, (offsetX / rect.width) * 100))
      setSliderValue(newValue)
    }
  }

  return ready ? (
    <div className="flex flex-col items-center">
      <div
        className="relative"
        style={{ width: size, height: size }}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div className="absolute inset-0 overflow-hidden">
          <div id="webar" className="w-full h-full" />
        </div>
        {!hideComparison && (
          <img
            src={image}
            alt="Old"
            className="absolute inset-0 w-full h-full  object-cover"
            style={{
              clipPath: hideComparison ? "none" : `inset(0 ${100 - sliderValue}% 0 0)`,
              opacity: showDelayedImage ? 1 : 0,
              transition: transitionStyle,
            }}
          />
        )}
        {!hideComparison && (
          <div
            className="absolute top-0 h-full "
            style={{
              left: `${sliderValue}%`,
              width: "2px",
              background: "#aaaac0",
              opacity: showDelayedImage ? 1 : 0,
              transition: transitionStyle,
            }}
          />
        )}
        {!hideComparison && (
          <div
            className="absolute bottom-1 transform -translate-x-1/2 cursor-grab "
            style={{
              left: `${sliderValue}%`,
              opacity: showDelayedImage ? 0.7 : 0,
              transition: transitionStyle,
            }}
            onMouseDown={handleMouseDown}
          >
            <button
              className="w-7 h-10 rotate-90 rounded-full !bg-white border border-gray-300 shadow-md flex items-center justify-center"
              style={{ cursor: isDragging ? "grabbing" : "grab" }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-gray-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="black"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 9l4-4 4 4m0 6l-4 4-4-4"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  ) : (
    <Box
      background="neutral200"
      className="flex items-center justify-center p-0"
      style={{ width: size, height: size }}
    >
      <Loader />
    </Box>
  )
}
